import { EVALUATION_FACTORS } from "./factors";

/**
 * Row builders shared by the CSV and XLSX exports on the results page.
 *
 * They live outside the component so the two exports cannot drift apart, and
 * so the shapes can be tested without mounting anything — the same reason
 * factors.js is a plain module.
 *
 * `formatDate` is injected rather than imported: the component already owns a
 * moment-based formatter, and passing it keeps a date library out of here.
 */

/** Blank rather than null, so an empty cell reads as empty in both formats. */
function blankIfNull(value) {
  return value == null ? "" : value;
}

/**
 * One row per respondent, every factor as its own column.
 *
 * This is the sheet the e-certificate run is generated from, so the leading
 * columns and their order are load-bearing.
 */
export function responseRows(responses, formatDate) {
  return (responses || []).map((response) => {
    const row = {
      Name: response.respondent_name,
      Email: response.respondent_email || "",
      Age: blankIfNull(response.age),
      Sex: response.sex,
      "Senior Citizen": response.senior_citizen ? "Yes" : "No",
      PWD: response.pwd ? "Yes" : "No",
      Modality: response.modality,
      Activity: response.activity_title || "",
      Average: blankIfNull(response.average),
      Submitted: formatDate(response.submitted_at),
    };
    const ratings = response.ratings || {};
    EVALUATION_FACTORS.forEach((factor) => {
      row[factor.label] = blankIfNull(ratings[factor.id]);
    });
    row.Comments = response.comments || "";
    return row;
  });
}

/**
 * The Summary sheet: what the results page shows above the response table.
 *
 * Built as an array-of-arrays because the sheet is three stacked tables with
 * different widths, which a row-of-objects shape cannot express.
 */
export function summaryAoa(summary, activityTitle, formatDate) {
  const stats = summary || {};
  const modality = stats.modality || {};
  const demographics = stats.demographics || {};
  const factors = stats.factors || {};
  const comments = stats.comments || [];

  const aoa = [
    ["Activity", activityTitle || ""],
    ["Total responses", stats.total_responses || 0],
    ["Overall average", blankIfNull(stats.overall_average)],
    ["On venue", modality["on-venue"] || 0],
    ["Virtual", modality.virtual || 0],
    ["Senior citizens", demographics.senior_citizen || 0],
    ["PWD", demographics.pwd || 0],
    ["Male", demographics.male || 0],
    ["Female", demographics.female || 0],
    [],
    ["Factor", "Average", "Responses", "Venue only"],
  ];

  EVALUATION_FACTORS.forEach((factor) => {
    const row = factors[factor.id] || {};
    aoa.push([
      factor.label,
      blankIfNull(row.average),
      row.responses || 0,
      factor.venueOnly ? "Yes" : "",
    ]);
  });

  aoa.push([]);
  if (!comments.length) {
    aoa.push(["No comments were left."]);
    return aoa;
  }
  aoa.push(["Comment", "Modality", "Submitted"]);
  comments.forEach((comment) => {
    aoa.push([
      comment.comments || "",
      comment.modality || "",
      formatDate(comment.submitted_at),
    ]);
  });
  return aoa;
}
